import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SIGNATURE_COLOR_LIST } from '../../../modules/memos/service/upload-memo.constants';
import { base64ToFile } from '../../../core/services/utils.service';
import { CanvasWhiteboardComponent } from 'ng2-canvas-whiteboard';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import { AlertService } from '../../../core/services/alert.service';
import { TranslateService } from '@ngx-translate/core';
import { ThemeList } from '../../service/theme.service';
import { OtpModalComponent } from '../otp-modal/otp-modal.component';
import { TextSignatureComponent } from '../../../modules/profile/component/text-signature/text-signature.component';
import { ProfileService } from '../../../modules/profile/shared/profile.service';
import { buildFullName } from '../../utils/common.util';
import { Store } from '@ngxs/store';
import { ThemeService } from '../../../shared/service/theme.service';
import {
  CropperPosition,
  Dimensions,
  ImageCropperComponent,
} from 'ngx-image-cropper';
import { ImageProcessingService } from '../../service/image-processing.service';

@Component({
  selector: 'app-select-sign-method-modal',
  templateUrl: './select-sign-method-modal.component.html',
  styleUrls: ['./select-sign-method-modal.component.scss'],
})
export class SelectSignMethodModalComponent {
  /** Enable typing signature text */
  @Input() customTextSignature = false;
  @Input() enableUploadSignature = false;
  @Input() enableUsingStoredSignature = true;
  @Input() enableTextSignature = false;
  @Input() isSaved: boolean;
  @Input() otpEnable: boolean;
  @Input() isNeedSign: boolean;
  @Input() isNoneUser = false;
  @Input() themeList: ThemeList;
  @Input() header;
  @Input() numberOfSignature: number = null;
  @Input() countPage: number;

  @Input() signatureText: string;
  @Input() otp: string;
  @Output() otpChange = new EventEmitter<string>();
  @Input() memoType;
  @Input() memoId;

  @Input() signature: File;
  @Output() signatureChange = new EventEmitter<File>();

  @Input() signaturePreview: string;
  @Input() canSignNow = true;
  /** Set the signature thickness on canvas.
   * When set it the responsive thickness is not used
   */
  @Input() signatureThickness: number;
  @Output() confirm = new EventEmitter();
  @Output() closeModal = new EventEmitter();

  @Input() isUseStored: boolean;
  @Output() isUseStoredChange = new EventEmitter();

  @ViewChild('canvasWhiteboard', { static: false })
  canvasWhiteboard: CanvasWhiteboardComponent;
  @ViewChild('selectSignMethodModal', { static: false })
  selectSignMethodModal: ElementRef;
  @ViewChild('signSignature', { static: false })
  signSignature: ElementRef;
  @ViewChild('uploadUserSignature', { static: false })
  uploadUserSignature: ElementRef;
  @ViewChild(OtpModalComponent)
  otpModalComponent: OtpModalComponent;
  @ViewChild('imageCropper')
  imageCropperComponent: ImageCropperComponent;

  @Input() isUploadOther = false;
  @Input() userTypeOfOtp = null;

  // Sign now
  signImageBlob: any = null;
  blobOutput: any = '';
  croppedImage: any = '';
  imageChangedEvent: any = '';
  errorMsg: any = '';
  fileInput: any = '';
  signatureColorList = SIGNATURE_COLOR_LIST;
  selectedColor = this.signatureColorList[0];
  showColorPicker = false;
  isSignNow = false;
  isUpload = false;
  uploadOriginalFileName = '';

  selectSignMethodModalRef: NgbModalRef;
  showPopupLine = false;
  cropper: CropperPosition = {
    x1: 0,
    y1: 0,
    x2: 0,
    y2: 0,
  };

  constructor(
    private profileService: ProfileService,
    public modalService: NgbModal,
    private alert: AlertService,
    public translate: TranslateService,
    private themeService: ThemeService,
    private store: Store,
    private imageProcessingService: ImageProcessingService,
  ) {
    this.store.selectSnapshot((store) => {
      if (store?.auth?.otp_type) {
        this.userTypeOfOtp = store?.auth?.otp_type;
      }
    });
  }

  /**
   * Calculate signature thickness from the width of canvas.
   * This calculation base on A4 paper (H.297mm x W.210mm).
   * Typically the signature canvas width is about 17.5% of paper (36.75 mm).
   * The common pen ballpoint is 0.5mm.
   * So, a signature thickness is about 1.58% (0.5 / 31.5) of the signature canvas width.
   * @param canvasWidth The width of canvas of signature
   * @returns The thickness of the signature
   */
  calcSignatureThickness(canvasWidth: number): number {
    return canvasWidth * (0.5 / 36.75);
  }

  // TODO - There is the unused parameter
  getSignatureThickness(canvasWidth: number): number {
    return this.signatureThickness || 10;
  }

  openOtpModal(): void {
    this.modalService.dismissAll();
    let otpType = this.userTypeOfOtp ? this.userTypeOfOtp : null;
    if (this.memoType === 'contract') {
      otpType = 'sms';
    }
    this.otpModalComponent.openModal(
      otpType,
      this.memoId,
      this.memoType,
    );
  }

  openSelectSignMethodModal(center = true): void {
    this.selectSignMethodModalRef = this.modalService.open(
      this.selectSignMethodModal,
      {
        backdrop: 'static',
        size: 'lg',
        centered: center,
      },
    );
  }

  openSignModal(): void {
    if (this.selectSignMethodModalRef) {
      this.selectSignMethodModalRef.dismiss('Cross Click');
    }
    const signModal = this.modalService.open(this.signSignature, {
      backdrop: 'static',
      keyboard: false,
      size: 'lg',
      centered: true,
    });
    signModal.result.finally(() => (this.isSignNow = false));
  }

  openUploadUserSignature(event): void {
    this.isUpload = true;
    this.signImageBlob = event.target.files[0];
    this.uploadOriginalFileName = this.signImageBlob.name;
    const reader = new FileReader();
    reader.readAsDataURL(event.target.files[0]);
    reader.onload = () => {};
    this.openSignModal();
  }

  onClearCanvas(): void {
    this.canvasWhiteboard.clearCanvas();
  }

  onUndoCanvas(): void {
    this.canvasWhiteboard.undo();
  }

  onClickColorPicker(): void {
    this.showColorPicker = !this.showColorPicker;
  }

  onSaveCanvas(): void {
    this.canvasWhiteboard.saveLocal();
  }

  onSelectedColor(color: string): void {
    this.selectedColor = color;
  }

  onSave(event): void {
    this.canvasWhiteboard.generateCanvasBlob((blob) => {
      this.blobOutput = blob;
      this.signImageBlob = blob;
      this.isSignNow = false;
    });
  }

  onSignNowClick(): void {
    this.isUseStoredChange.emit(false);
    this.resetSizeLine();
    this.isSignNow = true;
    this.uploadOriginalFileName = 'sign_now.png';
    this.openSignModal();
  }

  onTextSignatureClick(): void {
    if (this.selectSignMethodModalRef) {
      this.selectSignMethodModalRef.dismiss();
    }
    const profile = this.store.selectSnapshot((state) => state.auth);
    const modal = this.modalService.open(TextSignatureComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      keyboard: false,
    });
    modal.componentInstance.selectOnly = !this.customTextSignature;
    modal.componentInstance.fullName =
      this.signatureText ||
      buildFullName(profile.first_name, profile.last_name) ||
      '';
    const cancelClick = new EventEmitter();
    cancelClick.subscribe({
      next: () => this.reOpenSelectSignModal(),
    });
    const submitClick = new EventEmitter();
    submitClick.subscribe({
      next: (res) => modal.close(res),
    });
    modal.componentInstance.cancelClick = cancelClick;
    modal.componentInstance.signatureSaveClick = submitClick;
    modal.result
      .then((res) => {
        this.signatureChange.emit(res);
        if (this.otpEnable) {
          this.openOtpModal();
          return;
        }
        this.submit();
      })
      .catch(() => {});
  }

  imageCropped(output): void {
    this.croppedImage = output.base64;
    this.blobOutput = base64ToFile(
      output.base64,
      this.imageChangedEvent.target?.files[0]?.name,
    );
  }

  imageLoaded(): void {
    // show cropper
  }

  loadImageFailed(): void {
    this.imageChangedEvent = '';
    this.croppedImage = '';
    this.errorMsg = 'APPROVAL.ERROR-UNABLE-UPLOAD';
  }

  fileChangeEvent(file: any): void {
    this.errorMsg = '';
    this.imageChangedEvent = file;
    this.fileInput = file.target.files[0];
    this.uploadOriginalFileName = this.fileInput.name;
  }

  resetData(): void {
    this.isUpload = false;
    this.uploadOriginalFileName = '';
    this.isSignNow = false;
    this.signImageBlob = null;
    this.blobOutput = '';
    this.croppedImage = '';
    this.imageChangedEvent = '';
    this.errorMsg = '';
    this.fileInput = '';
  }

  clearSignature(): void {
    this.alert
      .confirm(this.translate.instant('ALERT.Are you sure'), ' ')
      .then((res) => {
        if (res.value) {
          this.reOpenSelectSignModal();
        }
      });
  }

  changeComplete(event): void {
    this.selectedColor = event.color.hex;
  }

  saveSignature(): void {
    this.signature = new File([this.blobOutput], 'online-sign.png', {
      type: this.blobOutput.type,
    });
    this.signatureChange.emit(this.signature);

    if (this.otpEnable) {
      this.openOtpModal();
    } else {
      this.submit();
    }
  }

  filePhotoChangeEvent(event: any): void {
    this.isUpload = true;
    this.errorMsg = '';
    this.signImageBlob = event.target.files[0];
    this.uploadOriginalFileName = this.signImageBlob.name;
    const reader = new FileReader();
    reader.readAsDataURL(event.target.files[0]);
    reader.onload = () => {};
    this.openSignModal();
    // Clear input file to avoid error when upload same file again
    event.target.value = '';
  }

  submit(useStoredSignature = false): void {
    this.isUseStoredChange.emit(useStoredSignature);
    this.confirm.emit(this.uploadOriginalFileName);
    this.resetData();
  }

  close(): void {
    this.modalService.dismissAll();
    this.closeModal.emit();
    this.resetData();
  }

  reOpenSelectSignModal(): void {
    this.modalService.dismissAll();
    this.resetData();
    this.openSelectSignMethodModal(true);
  }

  verifyOTP(): void {
    if (this.otpModalComponent.invalidOTP) {
      this.alert.error('Input OTP');
      return;
    }

    this.otpChange.emit(this.otp);
    this.submit();
  }

  onEditSizeLine(): void {
    this.showPopupLine = !this.showPopupLine;
  }

  resizeLine(event): void {
    this.getSignatureThickness(this.signatureThickness);
  }

  resetSizeLine(): void {
    if (this.themeService.isMobile()) {
      // I found that both iOS and Andriod have line thicker than desktop.
      this.signatureThickness = 6;
    } else {
      this.signatureThickness = 10;
    }
  }

  async onCropperReady(dimensions: Dimensions) {
    if (this.isUpload) {
      return;
    }
    this.cropper =
      await this.imageProcessingService.getSignatureBoundary(
        this.imageCropperComponent.imageFile,
      );
  }

  closeTool(): void {
    this.showColorPicker = false;
    this.showPopupLine = false;
  }
}
